import {useCallback, useEffect} from 'react';

import {CarouselAnalyticsProps} from 'components/recommendations/carousel-list-container/types';
import {CarouselEventMessagesType} from 'constants/recommendation';
import {BASKET_PDP_ATTRIBUTES} from 'contexts/analytics-context';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {Product, RecommendationResponse} from 'types';

interface UseCarouselEventHandlerProps {
  onSeeAllClick?: (analyticsPayload: any) => void;
  onEmptyCarousel?: (isEmpty: boolean) => void;
  updateBasket?: (data: any) => void;
  triggerCarouselScrollAnalytics?: (data: CarouselAnalyticsProps, direction: string) => void;
  setRecommendations?: (recommendation: RecommendationResponse) => void;
}

interface ICarouselMessage {
  product: Product;
  analytics: any;
}
export const useCarouselEventHandler = ({
  onSeeAllClick,
  onEmptyCarousel,
  updateBasket,
  triggerCarouselScrollAnalytics,
  setRecommendations
}: UseCarouselEventHandlerProps) => {
  const isReactNativeView = ReactNative.isReactNativeWebView();

  const _closeBasketDrawer = useCallback(() => {
    !isReactNativeView && SendBrowserEvent.closeBasketDrawer();
  }, [isReactNativeView]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        switch (event.data.type) {
          case CarouselEventMessagesType.IFRAME_READY: {
            //todo: set up initial scroll analytics for the carousel
            break;
          }
          case CarouselEventMessagesType.SEE_ALL: {
            const analyticsData = event.data.message;
            onSeeAllClick?.(analyticsData);
            _closeBasketDrawer();
            break;
          }
          case CarouselEventMessagesType.UPDATE_BASKET: {
            updateBasket?.(event?.data?.message);
            break;
          }
          case CarouselEventMessagesType.NAVIGATE_PDP_PAGE: {
            const productData = event.data.message as ICarouselMessage;
            const product = productData.product;

            SendBrowserEvent.goBasketRecommendationToPDP(product.id, productData.analytics);
            _closeBasketDrawer();

            break;
          }
          case CarouselEventMessagesType.SEND_RECOMMENDATIONS: {
            setRecommendations?.(event.data.message);
            break;
          }
          case CarouselEventMessagesType.TRIGGER_BASKET_ANALYTICS: {
            const analyticInfo = {
              ...event.data.params,
              pageName: BASKET_PDP_ATTRIBUTES.PAGE_NAME
            };
            SendBrowserEvent.triggerAddToCartAnalytics(event.data.eventName, analyticInfo);
            break;
          }
          case CarouselEventMessagesType.CAROUSEL_SCROLL: {
            triggerCarouselScrollAnalytics && triggerCarouselScrollAnalytics(event.data.message, event.data.direction);
            break;
          }
          case CarouselEventMessagesType.ADD_BASKET_ITEM_TO_LOCAL_STORAGE: {
            SendBrowserEvent.storeBasketItem(event.data.message);
            break;
          }
          case CarouselEventMessagesType.CAROUSEL_EMPTY: {
            const payload = event.data.message;

            onEmptyCarousel?.(payload);

            break;
          }
        }
      } catch (error) {
        console.error('Invalid message format:', error);
      }
    },
    [
      _closeBasketDrawer,
      onEmptyCarousel,
      onSeeAllClick,
      updateBasket,
      triggerCarouselScrollAnalytics,
      setRecommendations
    ]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  return {handleMessage};
};
